import { useCallback, useState } from 'react';

import { arrayMove } from '@dnd-kit/helpers';

import type { MarketAppWithTabs } from '@/features/Dashboard/types';
import { generateUniqueUUID } from '@/utils/generateUniqueUUID';

import { useDashboardEditActions } from '../../../hooks/useDashboardEditActions';
import { BLANK_COLUMN_ID } from '../types';

interface UseDragDropHandlersParams {
  currentTabId: string | null;
  marketTabApps: MarketAppWithTabs[];
}

interface UseDragDropHandlersResult {
  isDragging: boolean;
  handleDragStart: () => void;
  handleDragEnd: (event: any) => void;
  handleDragOver: (event: any) => void;
}

/**
 * Hook that encapsulates all drag-drop event handlers for AppsOrder.
 *
 * Handles:
 * - Tab → Blank Column: Creates new app with dropped tab
 * - Tab → Other App's Drop Zone: Moves tab between apps
 * - App ↔ App: Reorders apps
 * - Tab ↔ Tab (same app): Reorders tabs within app
 */
export const useDragDropHandlers = ({
  currentTabId,
  marketTabApps
}: UseDragDropHandlersParams): UseDragDropHandlersResult => {
  const actions = useDashboardEditActions();
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleDragEnd = useCallback(
    (event: any) => {
      const { source, target } = event.operation;
      if (!currentTabId) return;

      if (source?.type === 'item' || source?.type === 'singleItem') {
        handleTabDrop({
          source,
          target,
          currentTabId,
          marketTabApps,
          actions
        });
      }

      setIsDragging(false);
    },
    [currentTabId, marketTabApps, actions]
  );

  const handleDragOver = useCallback(
    (event: any) => {
      const { source, target } = event.operation;
      if (!currentTabId) return;
      if (target?.id === undefined) return;

      handleAppReorder({ source, target, marketTabApps, actions });
      handleTabReorder({ source, target, marketTabApps, actions, currentTabId });
    },
    [currentTabId, marketTabApps, actions]
  );

  return {
    isDragging,
    handleDragStart,
    handleDragEnd,
    handleDragOver
  };
};

const handleTabDrop = ({
  source,
  target,
  currentTabId,
  marketTabApps,
  actions
}: {
  source: any;
  target: any;
  currentTabId: string;
  marketTabApps: MarketAppWithTabs[];
  actions: ReturnType<typeof useDashboardEditActions>;
}) => {
  if (target?.id === BLANK_COLUMN_ID || target?.data?.group === BLANK_COLUMN_ID) {
    const itemId = source.id as string;
    const sourceApp = marketTabApps.find(app => app.tabs.some(t => t.id === itemId));
    if (!sourceApp || sourceApp.tabs.length === 1) return;

    const tab = sourceApp.tabs.find(t => t.id === itemId);
    if (!tab) return;

    const newAppId = generateUniqueUUID(new Set(marketTabApps.map(a => a.id)));
    actions.removeTabFromApp(currentTabId, sourceApp.id, itemId);
    actions.addApp(currentTabId, { id: newAppId, tabs: [tab] });
    return;
  }

  if (target?.type === 'column-item-drop') {
    const itemId = source.id as string;
    const targetAppId = target.data?.columnId as string;
    const sourceApp = marketTabApps.find(app => app.tabs.some(t => t.id === itemId));

    if (sourceApp && targetAppId && sourceApp.id !== targetAppId) {
      actions.moveTabBetweenApps(currentTabId, itemId, sourceApp.id, targetAppId);
    }
  }
};

const handleAppReorder = ({
  source,
  target,
  marketTabApps,
  actions
}: {
  source: any;
  target: any;
  marketTabApps: MarketAppWithTabs[];
  actions: ReturnType<typeof useDashboardEditActions>;
}) => {
  if (source?.type !== 'column' || target?.type !== 'column') return;
  if (target.id === BLANK_COLUMN_ID) return;

  const fromIndex = marketTabApps.findIndex(a => a.id === source.id);
  const toIndex = marketTabApps.findIndex(a => a.id === target.id);

  if (fromIndex !== -1 && toIndex !== -1 && fromIndex !== toIndex) {
    const reordered = arrayMove(marketTabApps, fromIndex, toIndex);
    actions.updateCurrentTabApps(reordered);
  }
};

const handleTabReorder = ({
  source,
  target,
  marketTabApps,
  actions,
  currentTabId
}: {
  source: any;
  target: any;
  marketTabApps: MarketAppWithTabs[];
  actions: ReturnType<typeof useDashboardEditActions>;
  currentTabId: string;
}) => {
  const sourceGroup = source ? source.data?.group : undefined;
  const targetGroup = target ? target.data?.group : undefined;

  if (source?.type !== 'item' || target?.type !== 'item' || sourceGroup !== targetGroup) return;

  const appId = sourceGroup as string;
  const app = marketTabApps.find(a => a.id === appId);
  if (!app) return;

  const fromIndex = app.tabs.findIndex(t => t.id === source.id);
  const toIndex = app.tabs.findIndex(t => t.id === target.id);

  if (fromIndex !== -1 && toIndex !== -1 && fromIndex !== toIndex) {
    actions.reorderTabsInsideApp(currentTabId, appId, fromIndex, toIndex);
  }
};
